import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { Notification, NotificationType } from '../../types/notification';
import { CreditCardIcon } from '../icons/Icons';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
  onViewAll: () => void;
}

const getIconForType = (type: NotificationType) => {
    switch (type) {
        case 'credits_low':
            return (
                <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            );
        case 'credits_expiring':
            return (
                <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'credits_added':
            return (
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'task_completed':
            return (
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'task_failed':
            return (
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'video_ready':
            return (
                <svg className="w-5 h-5 text-snapfy-accent-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
            );
        case 'welcome':
            return (
                <svg className="w-5 h-5 text-snapfy-accent-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
            );
        case 'promo':
            return (
                <svg className="w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
            );
        case 'system':
        default:
            return (
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
    }
};

const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose, anchorRef, onViewAll }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notifications, isLoading, fetchNotifications, markAsRead } = useNotificationStore();

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.readAt) {
      await markAsRead([notification.id]);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const recentNotifications = notifications.slice(0, 5);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={modalRef}
      className="absolute bottom-full mb-2 w-80 bg-snapfy-surface-hover rounded-xl shadow-lg border border-snapfy-border z-50 text-snapfy-text-primary"
      role="dialog"
      aria-modal="true"
    >
      <div className="p-4 border-b border-snapfy-border flex items-center justify-between">
        <h3 className="font-semibold text-lg">Notificações</h3>
        {notifications.length > 0 && (
          <span className="text-xs text-snapfy-text-secondary bg-snapfy-inner px-2 py-1 rounded-full">
            {notifications.length}
          </span>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-snapfy-text-secondary">Carregando...</div>
        ) : recentNotifications.length === 0 ? (
          <div className="p-8 text-center text-snapfy-text-secondary">
            <CreditCardIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhuma notificação</p>
          </div>
        ) : (
          recentNotifications.map(notification => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`flex items-start gap-4 p-4 border-b border-snapfy-border hover:bg-snapfy-inner/50 cursor-pointer ${
                !notification.readAt ? 'bg-[#1a0a3e]/10' : ''
              }`}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-snapfy-inner flex items-center justify-center mt-1">
                {getIconForType(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${!notification.readAt ? 'text-snapfy-text-primary' : 'text-snapfy-text-primary'}`}>
                  {notification.title}
                </p>
                <p className={`text-xs mt-0.5 ${!notification.readAt ? 'text-snapfy-text-secondary' : 'text-snapfy-text-secondary'}`}>
                  {notification.message}
                </p>
                <p className="text-xs text-snapfy-text-secondary mt-1">
                  {formatDistanceToNow(new Date(notification.createdAt), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </p>
              </div>
              {!notification.readAt && (
                <div className="w-2.5 h-2.5 rounded-full bg-[#7063f7] mt-1.5 flex-shrink-0"></div>
              )}
            </div>
          ))
        )}
      </div>
      {notifications.length > 5 && (
        <div className="p-2 text-center border-t border-snapfy-border">
          <button onClick={onViewAll} className="text-sm text-snapfy-accent-soft hover:underline">
            Ver todas ({notifications.length})
          </button>
        </div>
      )}
      {notifications.length <= 5 && notifications.length > 0 && (
        <div className="p-2 text-center">
          <button onClick={onViewAll} className="text-sm text-snapfy-accent-soft hover:underline">Ver todas</button>
        </div>
      )}
    </div>
  );
};

export default NotificationModal;
